/**
 * Aggregate cycle statistics for the Insights page.
 *
 * Peak-derived figures are labelled as post-peak rather than luteal: Peak Day is a proxy for
 * ovulation, not a measured ovulation date, so anything measured from it is an estimate.
 *
 * The fertile window figures are averages of each cycle's OWN method-derived window (see
 * fertileWindow.ts), not a calendar rule applied to the average cycle — a window that opened
 * on mucus onset in one cycle and a High reading in the next is still averaged as observed.
 */

import { getCycleLength, type PhaseCycle } from "./cyclePhases";
import type { NFPMethod } from "./cycleLogic";
import { getTodayKey, isPastCycle, isUpcomingCycle } from "./cycleTiming";
import {
  computeFertileWindow,
  getOvulationAnchoredWindow,
  type FertileWindowObservation,
  type WindowStartBasis,
} from "./fertileWindow";

export interface StatsCycle extends PhaseCycle {
  id: string;
}

export interface StatsObservation extends FertileWindowObservation {
  cycle_id: string;
}

export interface CycleStatsOptions {
  methods?: NFPMethod[];
  isPostpartum?: boolean;
  /**
   * Today's date key (owner's timezone). Defaults to the device's today. Decides which cycles
   * are completed (ended before today) and which are upcoming (pre-logged, excluded).
   */
  today?: string;
}

export interface CycleStats {
  /** Cycles with a known end date — the only ones a length can be measured from. */
  completedCount: number;
  avgLength: number | null;
  shortestLength: number | null;
  longestLength: number | null;

  /** Cycles with an identified Peak Day (may include the running cycle). */
  peakCount: number;
  avgPeakDay: number | null;
  earliestPeakDay: number | null;
  latestPeakDay: number | null;
  /** Peak + 1, the usual interval from Peak Day/LH surge to ovulation. */
  estimatedOvulationCd: number | null;

  /** Cycles with at least one positive ovulation test (OPK); cycles without one are ignored. */
  lhSurgeCount: number;
  avgLhSurgeCd: number | null;

  /** Completed cycles with a Peak — needed to measure Peak-to-end. */
  postPeakCount: number;
  avgPostPeakLength: number | null;
  shortestPostPeakLength: number | null;
  longestPostPeakLength: number | null;

  /** Averages of the per-cycle method-derived fertile windows. */
  fertileWindowCount: number;
  fertileWindowStartCd: number | null;
  fertileWindowEndCd: number | null;
  fertileWindowLength: number | null;
  /** The rule that opened most of those windows, so the UI can say why the window starts there. */
  fertileWindowStartBasis: WindowStartBasis | null;
  /** Ovulation − 5 through ovulation + 1, from gamete survival alone. */
  biologicalWindowStartCd: number | null;
  biologicalWindowEndCd: number | null;
}

const average = (values: number[]): number | null =>
  values.length ? Math.round(values.reduce((sum, v) => sum + v, 0) / values.length) : null;

const min = (values: number[]): number | null =>
  values.length ? Math.min(...values) : null;

const max = (values: number[]): number | null =>
  values.length ? Math.max(...values) : null;

export const computeCycleStats = (
  allCycles: StatsCycle[],
  observations: StatsObservation[] = [],
  options: CycleStatsOptions = {}
): CycleStats => {
  // A partial cycle (start never recorded — see PhaseCycle.start_inferred) has no real CD 1,
  // so its length, Peak day and window bounds are offsets from an arbitrary first logged
  // day. Folding them in would skew every average, so only recorded cycles are measured.
  //
  // Upcoming cycles (start after today — pre-logged or seeded data) haven't happened, and the
  // current cycle's end_date may only exist because a future start was logged. Neither is an
  // observed cycle: letting them into the averages would let unverified entries move the
  // shortest − 20 calculation and every window figure. So "completed" means ended before
  // today, and upcoming cycles are left out entirely.
  const today = options.today ?? getTodayKey();
  const cycles = allCycles.filter(
    (cycle) => !cycle.start_inferred && !isUpcomingCycle(cycle, today)
  );
  const completedCycles = cycles.filter((cycle) => isPastCycle(cycle, today));

  const lengths = completedCycles
    .map((cycle) => getCycleLength(cycle))
    .filter((length): length is number => length !== null);

  // Cycles without a detected Peak are excluded rather than counted as zero — a cycle where
  // no Peak was identified (common postpartum, or with sparse logging) has no peak day to
  // average, and folding it in as 0 would drag every peak-derived number down.
  const peakDays = cycles
    .map((cycle) => cycle.first_peak_cd)
    .filter((cd): cd is number => typeof cd === "number" && cd > 0);

  const postPeakLengths = completedCycles
    .map((cycle) => {
      const length = getCycleLength(cycle);
      const peakCd = cycle.first_peak_cd;
      if (!length || !peakCd || peakCd >= length) return null;
      return length - peakCd;
    })
    .filter((length): length is number => length !== null);

  const shortestCycleLength = min(lengths);

  const observationsByCycleId = new Map<string, StatsObservation[]>();
  for (const obs of observations) {
    const forCycle = observationsByCycleId.get(obs.cycle_id) ?? [];
    forCycle.push(obs);
    observationsByCycleId.set(obs.cycle_id, forCycle);
  }

  // Only windows that actually closed are averaged; an unresolved window has no end to
  // average and would otherwise pull the typical window artificially long.
  const windows = cycles.map((cycle) =>
    computeFertileWindow(observationsByCycleId.get(cycle.id) ?? [], options.methods, {
      peakCd: cycle.first_peak_cd,
      shortestCycleLength,
      completedCycleCount: completedCycles.length,
      isPostpartum: options.isPostpartum,
    })
  );
  const resolvedWindows = windows.filter((window) => window.endCd !== null);

  // Positive OPKs are corroboration, not a method sign, so they're aggregated on their own
  // and never folded into the Peak or window figures.
  const lhSurgeDays = windows
    .map((window) => window.lhSurgeCd)
    .filter((cd): cd is number => cd !== null);

  const avgPeakDay = average(peakDays);
  const estimatedOvulationCd = avgPeakDay ? avgPeakDay + 1 : null;
  const biologicalWindow = getOvulationAnchoredWindow(estimatedOvulationCd);

  const windowStart = average(resolvedWindows.map((w) => w.startCd));
  const windowEnd = average(resolvedWindows.map((w) => w.endCd as number));

  const basisCounts = new Map<WindowStartBasis, number>();
  for (const window of resolvedWindows) {
    basisCounts.set(window.startBasis, (basisCounts.get(window.startBasis) ?? 0) + 1);
  }
  const dominantStartBasis =
    [...basisCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;

  return {
    completedCount: completedCycles.length,
    avgLength: average(lengths),
    shortestLength: shortestCycleLength,
    longestLength: max(lengths),

    peakCount: peakDays.length,
    avgPeakDay,
    earliestPeakDay: min(peakDays),
    latestPeakDay: max(peakDays),
    estimatedOvulationCd,

    lhSurgeCount: lhSurgeDays.length,
    avgLhSurgeCd: average(lhSurgeDays),

    postPeakCount: postPeakLengths.length,
    avgPostPeakLength: average(postPeakLengths),
    shortestPostPeakLength: min(postPeakLengths),
    longestPostPeakLength: max(postPeakLengths),

    fertileWindowCount: resolvedWindows.length,
    fertileWindowStartCd: windowStart,
    fertileWindowEndCd: windowEnd,
    fertileWindowLength:
      windowStart && windowEnd && windowEnd >= windowStart
        ? windowEnd - windowStart + 1
        : null,
    fertileWindowStartBasis: dominantStartBasis,
    biologicalWindowStartCd: biologicalWindow?.startCd ?? null,
    biologicalWindowEndCd: biologicalWindow?.endCd ?? null,
  };
};
